import { useComputedColorScheme } from "@mantine/core";
import type { JsonServiceClient } from "@servicestack/client";
import { useMemo, type FC, type PropsWithChildren } from "react";

import DefaultClient from "src/api/client";
import ServiceStackClientContext from "src/contexts/api/ServiceStackClientContext";

/**
 * Supplies optional endpoint overrides for the shared ServiceStack client.
 */
interface IServiceStackClientProviderProps extends PropsWithChildren {
    baseUrl?: string;
    basePath?: string;
}

/**
 * Publishes a client configured for the endpoint and current computed color scheme.
 *
 * Postconditions: Memoization reuses the client while endpoint and theme dependencies match.
 * Client construction delegates headers and exception handling to DefaultClient; it does not
 * complete a network request or establish authentication.
 */
const ServiceStackClientProvider: FC<IServiceStackClientProviderProps> = ({
    children,
    baseUrl,
    basePath
}) => {
    const colorScheme = useComputedColorScheme();

    const client = useMemo(
        () => {
            return DefaultClient(baseUrl, basePath, colorScheme);
        },
        [baseUrl, basePath, colorScheme]
    );

    return (
        <ServiceStackClientContext.Provider value={client}>
            {children}
        </ServiceStackClientContext.Provider>
    );
};

export default ServiceStackClientProvider;
